import type { BackendModule, ReadCallback } from 'i18next'

/**
 * Languages with locale bundles under ./locales/<lang>/.
 * Used as supportedLngs so the lazy backend never requests a chunk that
 * doesn't exist (e.g. a detected "en-US").
 */
export const SUPPORTED_LANGUAGES = ['en', 'de', 'es', 'fr'] as const

/**
 * Loads a single locale module. Extracted so the backend's import strategy
 * can be unit-tested with an injected loader.
 */
export function loadLocaleModule(language: string, namespace: string): Promise<unknown> {
    // Vite turns this dynamic-import pattern into one lazy chunk per locale
    // JSON file, so production only downloads the active language's
    // namespaces instead of bundling all languages eagerly (TRA-115).
    return import(`./locales/${language}/${namespace}.json`)
}

type LocaleLoader = (language: string, namespace: string) => Promise<unknown>

/**
 * Creates an i18next backend that resolves namespaces via dynamic import.
 */
export function createLazyLocaleBackend(loader: LocaleLoader = loadLocaleModule): BackendModule {
    return {
        type: 'backend',
        init() {
            // No configuration needed; required by the BackendModule interface.
        },
        read(language: string, namespace: string, callback: ReadCallback) {
            loader(language, namespace)
                .then((mod) => {
                    const moduleData = mod as { default?: Parameters<ReadCallback>[1] }
                    callback(null, moduleData.default ?? moduleData)
                })
                .catch((error: unknown) => {
                    callback(error as Error, false)
                })
        },
    }
}
